import authService from './authService'
import logger from '@/utils/logger'

const { apiClient } = authService

/**
 * Blog Service for Content Management System
 *
 * Applying Service Layer pattern from PATTERNS_FROM_BASKET_BASE.md
 * Provides centralized API communication for blog operations
 *
 * Features:
 * - Complete CRUD operations for blog posts
 * - Category management
 * - Image upload with Cloudinary integration
 * - SEO optimization and validation
 * - Search and filtering capabilities
 * - Draft and publishing workflow
 * - Uses shared apiClient with CSRF protection and token refresh
 */

class BlogService {

  // ==========================================
  // BLOG POSTS OPERATIONS
  // ==========================================

  /**
   * Get paginated list of blog posts
   */
  async getPosts(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      
      // Pagination
      if (params.page) queryParams.append('page', params.page)
      if (params.limit) queryParams.append('limit', params.limit)
      
      // Filtering
      if (params.status) queryParams.append('status', params.status)
      if (params.category) queryParams.append('category', params.category)
      if (params.author) queryParams.append('author', params.author)
      if (params.featured !== undefined) queryParams.append('featured', params.featured)
      
      // Search
      if (params.search) queryParams.append('search', params.search)
      
      // Sorting
      if (params.sortBy) queryParams.append('sortBy', params.sortBy)
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder)

      const url = `/blog/posts${queryParams.toString() ? '?' + queryParams.toString() : ''}`
      const response = await apiClient.get(url)

      logger.debug('Blog posts fetched', {
        params,
        total: response.data.data?.total,
        count: response.data.data?.posts?.length
      })

      return response.data.data
    } catch (error) {
      logger.error('Failed to fetch blog posts', error)
      throw this.handleError(error)
    }
  }

  /**
   * Get single blog post by ID or slug
   */
  async getPost(identifier, isSlug = false) {
    try {
      // Backend auto-detects slug vs ID based on ObjectId validation
      const url = `/blog/posts/${identifier}`

      const response = await apiClient.get(url)

      logger.debug('Blog post fetched', { identifier, isSlug })
      return response.data.data
    } catch (error) {
      logger.error('Failed to fetch blog post', { identifier, error })
      throw this.handleError(error)
    }
  }

  /**
   * Create new blog post
   */
  async createPost(postData) {
    try {
      // Validate required fields
      this.validatePostData(postData)

      const response = await apiClient.post('/blog/posts', postData)

      logger.info('Blog post created', {
        id: response.data.data?.post?.id,
        title: response.data.data?.post?.title
      })

      return response.data.data
    } catch (error) {
      logger.error('Failed to create blog post', error)
      throw this.handleError(error)
    }
  }

  /**
   * Update existing blog post
   */
  async updatePost(id, postData) {
    try {
      // Validate required fields
      this.validatePostData(postData, false)

      const response = await apiClient.put(`/blog/posts/${id}`, postData)

      logger.info('Blog post updated', { id, title: response.data.data?.post?.title })
      return response.data.data
    } catch (error) {
      logger.error('Failed to update blog post', { id, error })
      throw this.handleError(error)
    }
  }

  /**
   * Delete blog post
   */
  async deletePost(id) {
    try {
      const response = await apiClient.delete(`/blog/posts/${id}`)

      logger.info('Blog post deleted', { id })
      return response.data.data
    } catch (error) {
      logger.error('Failed to delete blog post', { id, error })
      throw this.handleError(error)
    }
  }

  /**
   * Publish/unpublish blog post
   */
  async togglePostStatus(id, status) {
    try {
      if (!['published', 'draft', 'archived'].includes(status)) {
        throw new Error('Invalid post status')
      }

      const response = await apiClient.patch(`/blog/posts/${id}/status`, { status })

      logger.info('Blog post status updated', { id, status })
      return response.data.data
    } catch (error) {
      logger.error('Failed to update post status', { id, status, error })
      throw this.handleError(error)
    }
  }

  /**
   * Toggle featured status
   */
  async toggleFeatured(id, featured) {
    try {
      const response = await apiClient.patch(`/blog/posts/${id}/featured`, { featured })

      logger.info('Blog post featured status updated', { id, featured })
      return response.data.data
    } catch (error) {
      logger.error('Failed to update featured status', { id, featured, error })
      throw this.handleError(error)
    }
  }

  // ==========================================
  // CATEGORIES OPERATIONS
  // ==========================================

  /**
   * Get all blog categories
   */
  async getCategories() {
    try {
      const response = await apiClient.get('/blog/categories')

      logger.debug('Blog categories fetched', { count: response.data.data?.categories?.length })
      return response.data.data
    } catch (error) {
      logger.error('Failed to fetch blog categories', error)
      throw this.handleError(error)
    }
  }

  /**
   * Create new category
   */
  async createCategory(categoryData) {
    try {
      this.validateCategoryData(categoryData)

      const response = await apiClient.post('/blog/categories', categoryData)

      logger.info('Blog category created', {
        id: response.data.data.category._id,
        name: response.data.data.category.name
      })

      return response.data.data
    } catch (error) {
      logger.error('Failed to create blog category', error)
      throw this.handleError(error)
    }
  }

  /**
   * Update category
   */
  async updateCategory(id, categoryData) {
    try {
      this.validateCategoryData(categoryData, false)

      const response = await apiClient.put(`/blog/categories/${id}`, categoryData)

      logger.info('Blog category updated', { id, name: response.data.data.category.name })
      return response.data.data
    } catch (error) {
      logger.error('Failed to update blog category', { id, error })
      throw this.handleError(error)
    }
  }

  /**
   * Delete category
   */
  async deleteCategory(id) {
    try {
      const response = await apiClient.delete(`/blog/categories/${id}`)

      logger.info('Blog category deleted', { id })
      return response.data.data
    } catch (error) {
      logger.error('Failed to delete blog category', { id, error })
      throw this.handleError(error)
    }
  }

  // ==========================================
  // IMAGE OPERATIONS
  // ==========================================

  /**
   * Upload image to Vercel Blob Storage
   */
  async uploadImage(formData) {
    try {
      const response = await apiClient.post(
        '/blog/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )

      logger.info('Image uploaded successfully', {
        publicId: response.data.data.image.publicId,
        size: response.data.data.image.size
      })

      return response.data.data
    } catch (error) {
      logger.error('Failed to upload image', error)
      throw this.handleError(error)
    }
  }

  /**
   * Delete image from Vercel Blob Storage
   */
  async deleteImage(publicId) {
    try {
      const response = await apiClient.delete(`/blog/upload/${publicId}`)

      logger.info('Image deleted successfully', { publicId })
      return response.data.data
    } catch (error) {
      logger.error('Failed to delete image', { publicId, error })
      throw this.handleError(error)
    }
  }

  // ==========================================
  // SEARCH AND ANALYTICS
  // ==========================================

  /**
   * Search blog posts
   */
  async searchPosts(query, filters = {}) {
    try {
      const params = {
        search: query,
        ...filters
      }

      const response = await this.getPosts(params)

      logger.debug('Blog search performed', { query, results: response.total })
      return response
    } catch (error) {
      logger.error('Failed to search blog posts', { query, error })
      throw this.handleError(error)
    }
  }

  /**
   * Get blog analytics/stats
   */
  async getStats() {
    try {
      const response = await apiClient.get('/blog/stats')

      logger.debug('Blog stats fetched')
      return response.data.data
    } catch (error) {
      logger.error('Failed to fetch blog stats', error)
      throw this.handleError(error)
    }
  }

  /**
   * Increment post views
   */
  async incrementViews(id) {
    try {
      const response = await apiClient.patch(`/blog/posts/${id}/views`)

      return response.data.data
    } catch (error) {
      // Don't log view increment errors as they're not critical
      logger.debug('Failed to increment post views', { id, error })
      return null
    }
  }

  // ==========================================
  // CATEGORY OPERATIONS
  // ==========================================

  /**
   * Get all categories
   */
  async getCategories() {
    try {
      const response = await apiClient.get('/blog/categories')

      logger.debug('Blog categories fetched', {
        count: response.data.data?.categories?.length
      })

      return response.data.data
    } catch (error) {
      logger.error('Failed to fetch blog categories', error)
      throw this.handleError(error)
    }
  }

  /**
   * Get single category by ID or slug
   */
  async getCategory(identifier, isSlug = true) {
    try {
      const url = isSlug
        ? `/blog/categories/slug/${identifier}`
        : `/blog/categories/${identifier}`

      const response = await apiClient.get(url)

      logger.debug('Blog category fetched', { identifier, isSlug })
      return response.data.data
    } catch (error) {
      logger.error('Failed to fetch blog category', { identifier, error })
      throw this.handleError(error)
    }
  }

  // ==========================================
  // VALIDATION HELPERS
  // ==========================================

  /**
   * Validate post data
   */
  validatePostData(postData, isCreate = true) {
    const errors = []

    // Required fields for creation
    if (isCreate) {
      if (!postData.title?.trim()) {
        errors.push('Title is required')
      }
      if (!postData.content?.trim()) {
        errors.push('Content is required')
      }
    }

    // Validate title length
    if (postData.title && postData.title.length > 200) {
      errors.push('Title cannot exceed 200 characters')
    }

    // Validate excerpt length
    if (postData.excerpt && postData.excerpt.length > 300) {
      errors.push('Excerpt cannot exceed 300 characters')
    }

    // Validate SEO fields
    if (postData.seo) {
      if (postData.seo.metaTitle && postData.seo.metaTitle.length > 60) {
        errors.push('Meta title cannot exceed 60 characters')
      }
      if (postData.seo.metaDescription && postData.seo.metaDescription.length > 160) {
        errors.push('Meta description cannot exceed 160 characters')
      }
    }

    // Validate status
    if (postData.status && !['draft', 'published', 'archived'].includes(postData.status)) {
      errors.push('Invalid post status')
    }

    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join(', ')}`)
    }
  }

  /**
   * Validate category data
   */
  validateCategoryData(categoryData, isCreate = true) {
    const errors = []

    if (isCreate && !categoryData.name?.trim()) {
      errors.push('Category name is required')
    }

    if (categoryData.name && categoryData.name.length > 50) {
      errors.push('Category name cannot exceed 50 characters')
    }

    if (categoryData.description && categoryData.description.length > 200) {
      errors.push('Category description cannot exceed 200 characters')
    }

    if (categoryData.color && !/^#[0-9A-F]{6}$/i.test(categoryData.color)) {
      errors.push('Category color must be a valid hex color')
    }

    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join(', ')}`)
    }
  }

  /**
   * Handle API errors
   */
  handleError(error) {
    if (error.response) {
      // API returned an error response
      const message = error.response.data?.message || error.response.data?.error || 'API request failed'
      return new Error(message)
    } else if (error.request) {
      // Network error
      return new Error('Network error - please check your connection')
    } else {
      // Other error
      return error
    }
  }

  // ==========================================
  // UTILITY METHODS
  // ==========================================

  /**
   * Generate excerpt from content
   */
  generateExcerpt(content, maxLength = 200) {
    if (!content) return ''

    // Strip HTML tags
    const plainText = content.replace(/<[^>]*>/g, '')
    
    // Truncate to maxLength
    if (plainText.length <= maxLength) return plainText

    // Find last complete word within limit
    const truncated = plainText.substring(0, maxLength)
    const lastSpace = truncated.lastIndexOf(' ')
    
    return lastSpace > 0 
      ? truncated.substring(0, lastSpace) + '...'
      : truncated + '...'
  }

  /**
   * Extract SEO keywords from content
   */
  extractKeywords(content, title = '') {
    if (!content && !title) return []

    const text = `${title} ${content}`.toLowerCase()
    const plainText = text.replace(/<[^>]*>/g, '')
    
    // Simple keyword extraction (could be enhanced with NLP)
    const words = plainText.match(/\b\w{4,}\b/g) || []
    const wordCount = {}
    
    words.forEach(word => {
      if (word.length > 3) {
        wordCount[word] = (wordCount[word] || 0) + 1
      }
    })

    // Return top 10 most frequent words
    return Object.entries(wordCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10)
      .map(([word]) => word)
  }
}

// Export singleton instance
const blogService = new BlogService()
export default blogService